import { useEffect, useState } from "react";
import type { AgentFile } from "../types/api";
import { previewSizeAllowed, videoMimeType } from "../lib/filePreview";

type State =
  | { status: "idle" | "loading" }
  | { status: "ready"; url: string }
  | { status: "error"; message: string }
  | { status: "skipped"; message: string };

interface Props {
  file: AgentFile;
  fetchFile: (file: AgentFile) => Promise<Blob>;
  downloadFile: (file: AgentFile) => Promise<void>;
  labels: {
    download: string;
    load: string;
    loading: string;
    retry: string;
    tooLarge: string;
    unavailable: string;
  };
}

export function GeneratedVideoPreview({ file, fetchFile, downloadFile, labels }: Props) {
  // Authenticated files are fetched into a Blob before playback, so never spend
  // mobile bandwidth or memory until the user explicitly asks to load the video.
  const [armed, setArmed] = useState(false);
  const [state, setState] = useState<State>({ status: "idle" });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!armed) return;
    if (!previewSizeAllowed("video", file.bytes)) {
      setState({ status: "skipped", message: labels.tooLarge });
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ status: "loading" });
    fetchFile(file)
      .then((blob) => {
        if (cancelled) return;
        if (!previewSizeAllowed("video", blob.size)) {
          setState({ status: "skipped", message: labels.tooLarge });
          return;
        }
        const typedBlob = blob.type.startsWith("video/") ? blob : blob.slice(0, blob.size, videoMimeType(file));
        objectUrl = URL.createObjectURL(typedBlob);
        setState({ status: "ready", url: objectUrl });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", message: labels.unavailable });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [armed, fetchFile, file, labels.tooLarge, labels.unavailable, retryKey]);

  return (
    <div className="generated-video-card">
      <div className="generated-video-meta">
        <strong>{file.name}</strong>
        <button type="button" onClick={() => void downloadFile(file)}>{labels.download}</button>
      </div>
      <div className="generated-video-stage">
        {!armed ? (
          <button type="button" className="generated-video-load" onClick={() => setArmed(true)}>
            <span aria-hidden="true">▶</span>
            {labels.load}
          </button>
        ) : null}
        {state.status === "loading" ? <div className="generated-video-status"><span className="spinner" />{labels.loading}</div> : null}
        {state.status === "error" ? (
          <div className="generated-video-status">
            <span>{state.message}</span>
            <button type="button" onClick={() => setRetryKey((value) => value + 1)}>{labels.retry}</button>
          </div>
        ) : null}
        {state.status === "skipped" ? <div className="generated-video-status">{state.message}</div> : null}
        {state.status === "ready" ? (
          <video
            src={state.url}
            controls
            playsInline
            preload="metadata"
            onError={() => {
              URL.revokeObjectURL(state.url);
              setState({ status: "error", message: labels.unavailable });
            }}
          />
        ) : null}
      </div>
    </div>
  );
}
